import { BehaviorSubject } from 'rxjs';
import { Ring } from '../../chain.class';
import { AbstractTable } from './table.class';
import { Col } from '../../cols/col.class';
import { SortState, SortScheme } from '../../../../utils/sorter.class';


export interface DefaultSort {
   colId: string;
   state: SortState;
}


export abstract class AbstractSorter<D extends object> implements Ring<D[]>  {
   public input: BehaviorSubject<D[]>;
   public output = new BehaviorSubject<D[]>([]);
   readonly defaultChain: SortScheme<D>;
   protected lastChain: SortScheme<D>;

   constructor(
      protected ctx: AbstractTable<D>,
      protected defaults: DefaultSort,
   ) {
      const current = ctx.cols.sortable.find($col => $col.sortable.state !== `none`);
      
      this.defaultChain =  this.getDefaultSortScheme(defaults);
      if (current === undefined) {
         this.defaultChain.leader.state = defaults.state;
         this.lastChain = this.defaultChain;
      } else {
         this.lastChain = this.getSortScheme(current, current.sortable.state);
      }
   }
   

   public subscribe() {
      return this.input.subscribe(rows => {
         this.sortByLast();
      });
   }


   public abstract sortByCol(col: Col<D>): void;
   public abstract sortByDefault(): void;
   public abstract sortByLast(): void;  


   protected clearOtherStates() {
      this.ctx.cols.sortable
         .filter(col => col.sortable !== this.lastChain.leader)
         .forEach(col => col.sortable.state = 'none');
   }


   protected getSortScheme(leader: Col<D>, state: SortState): SortScheme<D> {
      return {
         toState: state,
         leader: leader.sortable,
         chain: this.ctx.cols.sortable
            .filter(col => col.isVisible)
            .filter(col => col.order > leader.order)
            .sort((a, b) => a.order - b.order)
            .map(col => col.sortable)
      }
   }


   private getDefaultSortScheme(defaults: DefaultSort): SortScheme<D> {
      const defaultCol = this.ctx.cols.all.find($col => $col.sortable.id === defaults.colId);
      const cols = this.ctx.cols.sortable
         .filter(col => col.defaults.isVisible)
         .filter(col => col.sortable.isActive)
         .filter(col => col.defaults.order > defaultCol.defaults.order);

      return {
         toState: defaults.state,
         leader: defaultCol.sortable,
         chain: cols.map(col => col.sortable)
      }
   }
}
